export const DEFAULT_DISCOVERY_THEME = 'wildlife';

export const DISCOVERY_THEME_ORDER = ['wildlife', 'celestial', 'earth', 'cultural'];

const DISCOVERY_THEMES = {
  wildlife: {
    key: 'wildlife',
    label: 'Wildlife',
    brandSubtitle: 'Species timing, migrations, and habitat context',
    nav: {
      planner: 'Plan',
      events: 'Wildlife events',
      hotspots: 'Regions',
      catalog: 'Species',
      menu: ['planner', 'catalog', 'about'],
    },
    home: {
      eyebrow: 'Wildlife-first mode',
      title: 'Track the species, seasonal windows, and field cues that matter around this coast.',
      lead:
        'Use Tellus like a timing-aware field guide. Start from a location, then read nearby migrations, likely species groups, and the conditions that make a sighting window worth showing up for.',
      heroCta: 'Open the wildlife planner',
      secondaryCta: 'Browse species',
      secondaryPath: '/species',
      secondaryOverrides: { page: 1 },
      examples: ['Whale migrations', 'Bird pulses', 'Kelp forest wildlife', 'Dense hotspot cells'],
    },
    planner: {
      eyebrow: 'Wildlife planner',
      title: 'Pick a place, season, or animal and see what else lines up around it.',
      description:
        'This mode keeps species and habitat context in the foreground so you can move from a chosen place or animal into nearby wildlife signals and seasonal cues.',
      subjectLabel: 'animal',
      subjectPlaceholder: 'Sea otter, gray whale, mola mola…',
      followUpCta: 'Open species atlas',
      followUpPath: '/species',
      followUpOverrides: { page: 1 },
      contextTitle: 'What species context lines up with this date',
      notesTitle: 'What else to watch for',
      staticNotes: [
        'Wildlife mode blends nearby events with habitat cues and regional pattern notes.',
        'Use it when the core question is what species may emerge around a location or season.',
      ],
    },
    events: {
      eyebrow: 'Wildlife signals',
      title: 'Wildlife events',
      description:
        'Follow migrations, unusual concentrations, and seasonal wildlife moments that are useful to both curious visitors and people already watching a taxon closely.',
      panelTitle: 'Wildlife signals near this point',
      panelDescription:
        'This live panel pulls wildlife-focused Tellus events first, then falls back to the archive if the real-time service is unavailable.',
      sidebarTitle: 'Best for',
      sidebarCopy:
        'Trip planning, migration watching, and quickly scanning where wildlife activity is surfacing in a way the public can understand.',
    },
    hotspots: {
      title: 'Hotspots',
      description:
        'Scan dense wildlife cells quickly, then open the ones that look unusually alive or worth revisiting.',
    },
    catalog: {
      title: 'Species atlas',
      description:
        'Search directly when you know the taxon, or sweep the list to spot species that are generating outsize observation or hotspot density.',
      sidebarTitle: 'Read the signals',
      sidebarCopy:
        'Observation count shows raw attention, while hotspot and event counts are better for judging whether something is spreading in space or surfacing in public-facing moments.',
      cards: [],
    },
    eventCategories: ['wildlife'],
  },
  celestial: {
    key: 'celestial',
    label: 'Celestial',
    brandSubtitle: 'Sky timing, observing windows, and visible phenomena',
    nav: {
      planner: 'Plan',
      events: 'Sky events',
      hotspots: 'Viewing spots',
      catalog: 'Phenomena',
      menu: ['planner', 'catalog', 'about'],
    },
    home: {
      eyebrow: 'Celestial mode',
      title: 'Follow sky windows, bright targets, and seasonal observing moments.',
      lead:
        'Use this mode to find when the sky is likely to reward attention: bright-object visibility, meteor windows, dark-sky timing, and approachable phenomena for a given place.',
      heroCta: 'Open the sky planner',
      secondaryCta: 'Open phenomena',
      secondaryPath: '/species',
      secondaryOverrides: { page: 1 },
      examples: ['Aurora windows', 'Meteor showers', 'Jupiter and Venus', 'Milky Way season'],
    },
    planner: {
      eyebrow: 'Celestial planner',
      title: 'Choose a place or season and plan what is worth looking up for.',
      description:
        'Celestial mode centers sky timing and approachable observing targets so you can move from a place and date into phenomena, events, and viewing advice.',
      subjectLabel: 'phenomenon',
      subjectPlaceholder: 'Aurora, Jupiter, Orion, meteor shower, Milky Way core…',
      followUpCta: 'Open phenomena atlas',
      followUpPath: '/species',
      followUpOverrides: { page: 1 },
      contextTitle: 'What sky phenomena and observing cues fit this window',
      notesTitle: 'Skywatch notes',
      staticCards: [
        {
          title: 'Bright objects',
          body:
            'Use this mode to move from general sky windows into approachable targets like Jupiter, Venus, the Moon, and seasonal constellations.',
        },
        {
          title: 'Viewing quality',
          body:
            'Distance is less important than darkness, cloud cover, horizon quality, and whether the event happens at a human-friendly hour.',
        },
      ],
      staticNotes: [
        'Celestial mode is strongest for time-window planning and public-friendly astronomy moments.',
        'The phenomena atlas complements the event feed so you can keep browsing once the event list narrows.',
      ],
    },
    events: {
      eyebrow: 'Sky windows',
      title: 'Celestial events',
      description:
        'Use a sky-first view of Tellus when you want auroras, showers, eclipses, conjunctions, and other public-facing astronomy moments without wildlife cards competing for attention.',
      panelTitle: 'Celestial signals near this point',
      panelDescription:
        'This panel prioritizes sky events around the selected location and falls back to the Tellus archive if the live upcoming service is unavailable.',
      sidebarTitle: 'Best for',
      sidebarCopy:
        'Night-sky tourism, astronomy outreach, and anyone planning around a trip date who wants to know if a strong celestial window lines up.',
    },
    hotspots: {
      title: 'Viewing spots',
      description:
        'Use the same regional shell to think in terms of observing points and current sky-worthy signals instead of wildlife-only hotspots.',
    },
    catalog: {
      title: 'Phenomena atlas',
      description:
        'Use this page for the sky patterns and observing targets that deserve attention in this mode, from bright planets to meteor radiants and dark-sky season anchors.',
      sidebarTitle: 'How to use this',
      sidebarCopy:
        'Treat these as practical observing subjects. Pair them with the planner to line up season, darkness, and local viewing conditions before you head out.',
      cards: [
        {
          title: 'Jupiter',
          subtitle: 'Bright planet',
          summary:
            'A strong public target when it is high above the horizon, especially during opposition windows and evenings with stable seeing.',
        },
        {
          title: 'Venus',
          subtitle: 'Morning or evening star',
          summary:
            'An easy gateway object for casual observers. Best when you want a clear, bright target with little setup friction.',
        },
        {
          title: 'Moon',
          subtitle: 'Phases and features',
          summary:
            'The most accessible object for beginners and one of the most seasonally useful anchors for public sky interpretation.',
        },
        {
          title: 'Orion',
          subtitle: 'Seasonal constellation',
          summary:
            'Useful for winter-night orientation and for introducing a broader patch of sky rather than a single bright object.',
        },
        {
          title: 'Milky Way core',
          subtitle: 'Dark-sky season target',
          summary:
            'Best treated as a timing-and-darkness target. Visibility depends more on season, moonlight, and light pollution than on instrument choice.',
        },
        {
          title: 'Meteor shower radiants',
          subtitle: 'Event-linked targets',
          summary:
            'Useful when an event page tells you something is peaking and you want the broader object context behind that shower.',
        },
      ],
    },
    eventCategories: ['celestial'],
  },
  earth: {
    key: 'earth',
    label: 'Earth',
    brandSubtitle: 'Coasts, climate, tides, blooms, and Earth-system timing',
    nav: {
      planner: 'Plan',
      events: 'Earth events',
      hotspots: 'Regions',
      catalog: 'Systems',
      menu: ['planner', 'catalog', 'about'],
    },
    home: {
      eyebrow: 'Earth-systems mode',
      title: 'Read the coast through tides, blooms, storms, currents, and seasonal system shifts.',
      lead:
        'This mode is for process-first exploration: when you want to know what the shoreline, water column, atmosphere, or broader climate system is doing before you narrow down to one species response.',
      heroCta: 'Open the systems planner',
      secondaryCta: 'Open systems',
      secondaryPath: '/species',
      secondaryOverrides: { page: 1 },
      examples: ['Atmospheric rivers', 'King tides', 'Upwelling', 'Bloom conditions'],
    },
    planner: {
      eyebrow: 'Earth planner',
      title: 'Choose a place or season and see which Earth systems are lining up there.',
      description:
        'Earth mode emphasizes process and context over taxa. Use it when you care more about the system itself than any one species response.',
      subjectLabel: 'system',
      subjectPlaceholder: 'Upwelling, atmospheric river, king tide…',
      followUpCta: 'Open systems atlas',
      followUpPath: '/species',
      followUpOverrides: { page: 1 },
      contextTitle: 'What Earth-system cues fit this window',
      notesTitle: 'Field notes',
      staticCards: [
        {
          title: 'Coastal processes',
          body:
            'This mode is useful for tides, upwelling, plume events, surf energy, and the seasonal windows that make coastal systems feel different on the ground.',
        },
        {
          title: 'Big-pattern framing',
          body:
            'Use it to connect a specific trip or location to larger cycles like ENSO, atmospheric rivers, bloom periods, or smoke transport.',
        },
      ],
      staticNotes: [
        'Earth mode is best when the event itself is the thing you are studying, explaining, or planning around.',
        'It can still support wildlife reasoning, but it leads with system-first language instead of species-first labels.',
      ],
    },
    events: {
      eyebrow: 'Earth systems',
      title: 'Earth-system events',
      description:
        'Focus Tellus on coastlines, climate, and planetary systems when you want to understand what the Earth itself is doing rather than what one species is doing.',
      panelTitle: 'Earth-system signals near this point',
      panelDescription:
        'This panel favors Earth-system events around a location, then falls back to the archive if the live upcoming-events service is unavailable.',
      sidebarTitle: 'Best for',
      sidebarCopy:
        'Coastal interpretation, climate communication, environmental planning, and anyone looking for system-level context before species-level detail.',
    },
    hotspots: {
      title: 'Regional signals',
      description:
        'Think in terms of regions and process intensity here rather than pure wildlife concentration.',
    },
    catalog: {
      title: 'Systems atlas',
      description:
        'Browse the recurring Earth systems and coastal processes that shape what people actually experience on the shore, in the weather, and across the season.',
      sidebarTitle: 'How to use this',
      sidebarCopy:
        'Treat these as system briefings. Use them to understand what each process looks like in the field, why timing matters, and what nearby changes it tends to drive.',
      cards: [
        {
          title: 'Atmospheric rivers',
          subtitle: 'Moisture transport corridors',
          summary:
            'Important for flood risk, snowpack, and public planning. Best framed as both a weather event and a broader hydrologic process.',
        },
        {
          title: 'King tides',
          subtitle: 'Extreme high-tide windows',
          summary:
            'Useful for coastal interpretation, infrastructure awareness, and showing what future sea-level impacts may look like under present-day conditions.',
        },
        {
          title: 'Upwelling',
          subtitle: 'Cold nutrient-rich surfacing water',
          summary:
            'A core coastal process that changes productivity, water temperature, and how marine ecosystems feel on the surface.',
        },
        {
          title: 'Plankton blooms',
          subtitle: 'Productivity pulses',
          summary:
            'These can be beautiful, useful, or hazardous depending on context. Pair them with wind, temperature, and current conditions.',
        },
        {
          title: 'ENSO states',
          subtitle: 'El Nino / La Nina climate context',
          summary:
            'Use these for the large-pattern framing that helps explain why a season feels strange before you jump into a local event.',
        },
        {
          title: 'Smoke and plume transport',
          subtitle: 'Atmospheric movement',
          summary:
            'Relevant when the visual or health effects of an Earth event depend less on origin and more on where the air mass carries it.',
        },
      ],
    },
    eventCategories: ['earth_systems'],
  },
  cultural: {
    key: 'cultural',
    label: 'Cultural',
    brandSubtitle: 'Calendars, traditions, observances, and place-based knowledge',
    nav: {
      planner: 'Plan',
      events: 'Calendar',
      hotspots: 'Places & calendars',
      catalog: 'Traditions',
      menu: ['planner', 'events', 'catalog', 'about'],
    },
    home: {
      eyebrow: 'Cultural mode',
      title: 'Browse living calendars, seasonal observances, and place-linked traditions.',
      lead:
        'Use this mode to move between dated public observances, seasonal practice windows, and broader cultural context without flattening place-based traditions into generic trivia.',
      heroCta: 'Open the seasonal planner',
      secondaryCta: 'Open calendar',
      secondaryPath: '/events',
      secondaryOverrides: { page: 1 },
      examples: ['Hobiyee', 'First-salmon windows', 'Matariki', 'National Day of Mourning'],
    },
    planner: {
      eyebrow: 'Seasonal planner',
      title: 'Choose a place or time of year and browse the cultural calendars that may line up with it.',
      description:
        'Use cultural mode to connect dated observances, seasonal-practice windows, and place-linked traditions to the region and time of year you have in view.',
      subjectLabel: 'tradition',
      subjectPlaceholder: 'Hobiyee, first salmon, Matariki, mourning observance…',
      followUpCta: 'Open the cultural calendar',
      followUpPath: '/events',
      followUpOverrides: { page: 1 },
      contextTitle: 'What seasonal practices fit this window',
      notesTitle: 'Context notes',
      staticCards: [
        {
          title: 'Ancient and ongoing practice',
          body:
            'This mode is meant to hold older seasonal ways alongside modern civic and cultural events without flattening them into the same thing.',
        },
        {
          title: 'Place matters',
          body:
            'Use cultural mode to ask not just what is happening, but who has reason to notice this season, moon phase, harvest, or migration.',
        },
      ],
      staticNotes: [
        'The cultural layer now has source-backed Tellus event records, but it should still be read as a respectful public-context layer rather than a substitute for region-specific cultural authority.',
        'Many observances are local, relational, or invitation-based, so the feed should highlight season and place without pretending to exhaust living traditions.',
      ],
    },
    events: {
      eyebrow: 'Cultural calendars',
      title: 'Cultural and seasonal calendars',
      description:
        'This mode foregrounds cultural and seasonal practice records so Tellus can support tradition, remembrance, and public ritual, not only wildlife or Earth-system events.',
      panelTitle: 'Cultural signals near this point',
      panelDescription:
        'This panel prioritizes public-facing cultural observances and seasonal-practice signals around the selected location, then falls back to the archive if the live upcoming-events service is unavailable.',
      sidebarTitle: 'How to read this',
      sidebarCopy:
        'Treat this feed as a respectful public calendar. It is strongest for public observances, seasonal-practice windows, and remembrance dates, but it does not replace local guidance about invitation, protocol, or meaning.',
    },
    hotspots: {
      title: 'Places and calendars',
      description:
        'Use this mode to think about place-linked seasonal calendars rather than wildlife concentration cells.',
    },
    catalog: {
      title: 'Traditions atlas',
      description:
        'Use this page for tradition, remembrance, seasonal practice, and cultural timing context that benefits from slower reading than the live calendar feed.',
      sidebarTitle: 'How to use this',
      sidebarCopy:
        'These cards are orientation notes, not complete authorities. Use them to approach a place and season more respectfully, then keep following local context from there.',
      cards: [
        {
          title: 'Hobiyee and Nisga’a New Year',
          subtitle: 'Lunar winter renewal',
          summary:
            'A strong example of a lesser-known Indigenous New Year anchored to the first crescent moon and winter ceremonial life.',
        },
        {
          title: 'First-salmon return cycles',
          subtitle: 'Species-linked seasonal practice',
          summary:
            'A reminder that many cultural calendars are inseparable from the return of a species, not just from a fixed civic date.',
        },
        {
          title: 'Matariki / Maori New Year',
          subtitle: 'Night-sky and seasonal planning',
          summary:
            'Useful when you want a clear bridge between celestial timing, remembrance, renewal, and future planning.',
        },
        {
          title: 'Green Corn / Busk renewal',
          subtitle: 'Late-summer ceremonial reset',
          summary:
            'A less-publicized example of how crop stage, ritual cleansing, and communal renewal can anchor a seasonal calendar.',
        },
        {
          title: 'National Day of Mourning',
          subtitle: 'Remembrance and historical truth',
          summary:
            'A reminder that cultural calendars also hold grief, resistance, and public truth-telling, not only celebration.',
        },
        {
          title: 'Nalukataq and whaling thanksgiving',
          subtitle: 'Harvest gratitude and sharing',
          summary:
            'A place-based example of feast, gratitude, sharing, and ceremony following a species-linked subsistence season.',
        },
      ],
    },
    curatedEvents: [
      {
        id: 'cultural-hobiyee',
        title: 'Hobiyee and Nisga’a New Year',
        category: 'cultural',
        category_label: 'Cultural',
        summary:
          'A less-publicized Indigenous New Year anchored to the first crescent moon and still strongly tied to winter ceremonial life.',
        status: 'annual window',
        starts_at: 'Late winter lunar window',
        why_it_matters: ['Lunar timing', 'Winter renewal', 'Nisga’a ceremonial calendar'],
      },
      {
        id: 'cultural-first-salmon',
        title: 'First-salmon ceremony windows',
        category: 'cultural',
        category_label: 'Cultural',
        summary:
          'An important example of how the return of a species can anchor ceremony, gratitude, and stewardship responsibilities.',
        status: 'seasonal',
        starts_at: 'Spring return-cycle window',
        why_it_matters: ['Species and culture overlap', 'First foods', 'Stewardship commitments'],
      },
      {
        id: 'cultural-matariki',
        title: 'Matariki / Maori New Year',
        category: 'cultural',
        category_label: 'Cultural',
        summary:
          'A strong public-facing bridge between stars, remembrance, seasonal planning, food sharing, and Indigenous timekeeping.',
        status: 'annual',
        starts_at: 'Midwinter in Aotearoa',
        why_it_matters: ['Night-sky culture', 'Renewal', 'Remembrance'],
      },
      {
        id: 'cultural-day-of-mourning',
        title: 'National Day of Mourning',
        category: 'cultural',
        category_label: 'Cultural',
        summary:
          'A lesser-known observance that centers Indigenous memory, truth-telling, and public remembrance rather than celebration.',
        status: 'annual',
        starts_at: 'U.S. Thanksgiving Day',
        why_it_matters: ['Historical reframing', 'Public remembrance', 'Wampanoag-led context'],
      },
    ],
    eventCategories: ['cultural'],
  },
};

export function normalizeDiscoveryTheme(value) {
  return DISCOVERY_THEMES[value] ? value : DEFAULT_DISCOVERY_THEME;
}

export function readDiscoveryTheme(search = '') {
  const params = new URLSearchParams(search);
  return normalizeDiscoveryTheme(params.get('theme') || DEFAULT_DISCOVERY_THEME);
}

export function getDiscoveryTheme(value) {
  return DISCOVERY_THEMES[normalizeDiscoveryTheme(value)];
}

export function themeCategoryKey(theme) {
  const activeTheme = normalizeDiscoveryTheme(theme);
  return activeTheme === 'earth' ? 'earth_systems' : activeTheme;
}

export function buildThemeHref(pathname, currentSearch = '', nextTheme, overrides = {}) {
  const params = new URLSearchParams(currentSearch);
  params.set('theme', normalizeDiscoveryTheme(nextTheme));

  Object.entries(overrides).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      params.delete(key);
      return;
    }
    params.set(key, String(value));
  });

  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}`;
}

export function getThemeMenuItems(theme, currentSearch = '') {
  const activeTheme = normalizeDiscoveryTheme(theme);
  const config = getDiscoveryTheme(activeTheme);
  const menu = Array.isArray(config.nav?.menu) ? config.nav.menu : ['planner', 'catalog', 'about'];
  const menuWithDocs = menu.includes('docs') ? menu : [...menu, 'docs'];

  const builders = {
    planner: () => ({
      key: 'planner',
      label: config.nav.planner,
      to: buildThemeHref('/planner', currentSearch, activeTheme, { page: null, q: null }),
    }),
    events: () => ({
      key: 'events',
      label: config.nav.events,
      to: buildThemeHref('/events', currentSearch, activeTheme, { page: 1 }),
    }),
    hotspots: () => ({
      key: 'hotspots',
      label: config.nav.hotspots,
      to: buildThemeHref('/hotspots', currentSearch, activeTheme, { page: 1 }),
    }),
    catalog: () => ({
      key: 'catalog',
      label: config.nav.catalog,
      to: buildThemeHref('/species', currentSearch, activeTheme, { page: 1 }),
    }),
    about: () => ({
      key: 'about',
      label: 'About',
      to: buildThemeHref('/about', currentSearch, activeTheme, { page: null, q: null }),
    }),
    docs: () => ({
      key: 'docs',
      label: 'API docs',
      to: buildThemeHref('/docs', currentSearch, activeTheme, { page: null, q: null }),
    }),
  };

  return menuWithDocs.map((item) => builders[item]?.()).filter(Boolean);
}

export function getThemeRequestFilters(theme) {
  const category = themeCategoryKey(theme);
  return {
    interests: category ? [category] : [],
    categories: category ? [category] : [],
  };
}

function normalizeCategoryToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[\s/-]+/g, '_')
    .replace(/[^a-z0-9_,]/g, '');
}

function collectEventCategoryTokens(item) {
  const tokens = new Set();

  const addValue = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(addValue);
      return;
    }

    const normalized = normalizeCategoryToken(value);
    if (!normalized) return;

    normalized
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        tokens.add(part);
        if (part === 'earth_systems') tokens.add('earth');
        if (part === 'earth') tokens.add('earth_systems');
      });
  };

  addValue(item?.category);
  addValue(item?.category_label);
  addValue(item?.categories);
  addValue(item?.interests);
  addValue(item?.tags);

  return tokens;
}

export function filterEventsForTheme(events, theme) {
  const activeTheme = themeCategoryKey(theme);

  return (Array.isArray(events) ? events : []).filter((item) => {
    const tokens = collectEventCategoryTokens(item);
    return tokens.has(activeTheme);
  });
}

export function buildThemeEventPayload(payload, theme) {
  const themedEvents = filterEventsForTheme(payload?.events || [], theme);
  const activeCount = themedEvents.filter(
    (item) => String(item?.status || '').trim().toLowerCase() === 'active'
  ).length;

  return {
    ...(payload || {}),
    summary: {
      ...(payload?.summary || {}),
      event_count: themedEvents.length,
      active_count: activeCount,
      upcoming_count: Math.max(themedEvents.length - activeCount, 0),
    },
    events: themedEvents,
  };
}

export function getCuratedEventsForTheme(theme) {
  return getDiscoveryTheme(theme).curatedEvents || [];
}

export function isWildlifeTheme(theme) {
  return normalizeDiscoveryTheme(theme) === 'wildlife';
}
